import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getDynamicPTSProjects, createDynamicPTSProject,
  getKuntoarviovalikot, getTarkastuskohteetV2,
  createRakenneosaV2, getRakenneosatV2, getRakenneosaTyypit,
} from '../../../../../api/api';

// Service layer for the shared PTS-projekti -> tarkastuskohde -> rakenneosa
// navigation every julkisivu v2 measurement block uses. Owns all data/API
// calls; the view (RakenneosaNavView) only renders whatever this hook hands
// it. Tarkastuskohteet are only ever created in Kuntoarviovalikko — this
// picker just tabs between whichever ones already exist there, flattened
// across every category Kuntoarviovalikko activated (not grouped by
// category), so e.g. "Asuntojen parvekkeet" shows up as a tab directly.
//
// PTS-project selection always follows Kuntoarviovalikko's own choice when
// linkedProjectId is given (via findLinkedProjectId — same cross-block sync
// DynamicPTSTaulu already uses); a manual picker is only shown as a
// fallback when nothing is linked yet.
const NAV_KEYS = ['ptsProjectId', 'kavId', 'category', 'tkId', 'tkNimi', 'rakId', 'rakNimi'];

export default function useRakenneosaNavV2(id_kiinteisto, value, onChange, linkedProjectId = null, activeSectionName = null) {
  // The report's own save pipeline (ReportTemplateContainer's
  // handleBlockChange) doesn't update `value` synchronously when onChange
  // fires — it only updates once a debounced autosave round-trips — so
  // without this, clicking a tab felt laggy: it only became "active" (and
  // its data only started fetching) once that save resolved. changeNav
  // below applies the nav change to local state IMMEDIATELY, still calling
  // onChange to persist it exactly as before, then reconciles back to
  // `value` once that prop catches up. Deliberately does not touch
  // handleBlockChange/debouncedSave themselves — that save chain is
  // explicitly out of scope here.
  const [optimisticNav, setOptimisticNav] = useState(null);
  const propNav = value || {};
  useEffect(() => {
    if (!optimisticNav) return;
    const caughtUp = NAV_KEYS.every(k => (propNav[k] ?? null) === (optimisticNav[k] ?? null));
    if (caughtUp) setOptimisticNav(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, optimisticNav]);
  // Safety net: handleBlockChange can silently drop a change entirely (e.g.
  // the section is locked by another editor — it shows a popup and never
  // calls debouncedSave), in which case `value` would never catch up to
  // reconcile the effect above. Without this, the tab would stay stuck
  // showing the rejected switch as if it had worked. Any new changeNav call
  // (a fresh tab click) resets this timer.
  useEffect(() => {
    if (!optimisticNav) return;
    const t = setTimeout(() => setOptimisticNav(null), 4000);
    return () => clearTimeout(t);
  }, [optimisticNav]);
  const changeNav = useCallback((next) => { setOptimisticNav(next); onChange(next); }, [onChange]);

  const effectiveNav = optimisticNav ?? propNav;
  const { ptsProjectId: storedPtsProjectId = null, tkId = null, tkNimi = null, kavId = null, category = null, rakId = null, rakNimi = null } = effectiveNav;
  const ptsProjectId = linkedProjectId ?? storedPtsProjectId;

  const [ptsProjects, setPtsProjects] = useState([]);
  const [rakTyypit, setRakTyypit] = useState([]);
  const [existingKav, setExistingKav] = useState([]);
  const [tarkastuskohteet, setTarkastuskohteet] = useState([]);
  const [rakenneosat, setRakenneosat] = useState([]);
  // Which tkId `rakenneosat` actually belongs to — see the two effects
  // below for why this matters: React runs every effect whose deps changed
  // in ONE post-commit batch, each still closing over ITS OWN render's
  // state. On the very render where a tab switch lands (tkId already the
  // new one, rakId already null via optimistic nav), `refreshRak`'s effect
  // runs and calls `setRakenneosat([])` — but that update isn't visible
  // yet to the OTHER effects in this SAME batch; they still see the
  // PREVIOUS tarkastuskohde's real rakenneosat array. Without this tag,
  // the auto-select-first-rakenneosa effect below reads `rakenneosat` in
  // that same pass, sees it non-empty (the stale one), and immediately
  // re-selects the old tarkastuskohde's rakenneosa — showing its table
  // under the newly-selected tab. Tagging which tkId the array is FOR, and
  // requiring it to match the current tkId before either downstream
  // effect trusts the array, closes the gap: the tag itself starts
  // mismatched on that same render (still the old tkId) and only catches
  // up once refreshRak's update actually lands on a later render.
  const [rakenneosatTkId, setRakenneosatTkId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { getRakenneosaTyypit().then(r => setRakTyypit(r.data)).catch(() => {}); }, []);

  const refreshPtsProjects = useCallback(() => {
    if (!id_kiinteisto) { setPtsProjects([]); return; }
    getDynamicPTSProjects(id_kiinteisto).then(r => setPtsProjects(r.data || [])).catch(() => {});
  }, [id_kiinteisto]);
  useEffect(refreshPtsProjects, [refreshPtsProjects]);

  // Follow Kuntoarviovalikko's chosen project: whenever it differs from what
  // this block has stored, persist it here too and reset the downstream
  // chain (old tarkastuskohde/rakenneosa no longer apply once the project
  // itself changed) — same "linkedProjectId always wins" contract
  // DynamicPTSTaulu already implements.
  useEffect(() => {
    if (linkedProjectId && linkedProjectId !== storedPtsProjectId) {
      changeNav({ ptsProjectId: linkedProjectId, kavId: null, category: null, tkId: null, tkNimi: null, rakId: null, rakNimi: null });
    }
  }, [linkedProjectId, storedPtsProjectId, changeNav]);

  const pickPtsProject = useCallback((proj) => {
    changeNav({ ptsProjectId: proj.id, kavId: null, category: null, tkId: null, tkNimi: null, rakId: null, rakNimi: null });
  }, [changeNav]);

  const createPtsProject = useCallback(async (name) => {
    if (!id_kiinteisto || !name.trim()) return;
    setError('');
    try {
      const r = await createDynamicPTSProject(id_kiinteisto, name.trim(), new Date().getFullYear());
      refreshPtsProjects();
      changeNav({ ptsProjectId: r.data.id, kavId: null, category: null, tkId: null, tkNimi: null, rakId: null, rakNimi: null });
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  }, [id_kiinteisto, changeNav, refreshPtsProjects]);

  const refreshExistingKav = useCallback(async () => {
    if (!ptsProjectId) { setExistingKav([]); return []; }
    try {
      const r = await getKuntoarviovalikot(ptsProjectId);
      setExistingKav(r.data);
      return r.data;
    } catch { return []; }
  }, [ptsProjectId]);
  useEffect(() => { refreshExistingKav(); }, [refreshExistingKav]);

  const refreshTkFromKav = useCallback(async (kavList) => {
    if (!kavList.length) { setTarkastuskohteet([]); return []; }
    const lists = await Promise.all(kavList.map(kav =>
      getTarkastuskohteetV2(kav.id)
        .then(r => r.data.map(tk => ({ ...tk, kavId: kav.id, category: kav.category })))
        .catch(() => [])
    ));
    const flat = lists.flat().filter(tk => tk.is_active);
    setTarkastuskohteet(flat);
    return flat;
  }, []);
  const refreshTk = useCallback(() => refreshTkFromKav(existingKav), [existingKav, refreshTkFromKav]);
  // Effect callbacks must return either nothing or a cleanup function —
  // refreshTk (and refreshTkFromKav, now async since it returns the fresh
  // list for refreshTarkastuskohteet below) returns a Promise, which React
  // would otherwise try to call as that cleanup function on unmount
  // ("destroy is not a function"). Wrapping in a block body discards it.
  useEffect(() => { refreshTk(); }, [refreshTk]);

  // Exposed for callers that create a tarkastuskohde through a DIFFERENT
  // hook instance (e.g. useKuntoarviovalikkoV2, in the standalone kuntoarvio
  // workspace) — that doesn't change `ptsProjectId` here, so the effects
  // above never refire on their own. Refetches kuntoarviovalikko rows fresh
  // (a brand-new category's kav row wouldn't be in `existingKav` yet either)
  // and feeds that straight into the tarkastuskohde fetch, rather than
  // relying on `existingKav` state (stale until the next render). Returns
  // the fresh list too, so a caller that just created a tarkastuskohde can
  // find and auto-select it immediately, without waiting a render for state
  // to catch up.
  const refreshTarkastuskohteet = useCallback(async () => {
    const kavList = await refreshExistingKav();
    return refreshTkFromKav(kavList);
  }, [refreshExistingKav, refreshTkFromKav]);

  // Always holds the CURRENT tkId, read synchronously during render — same
  // "latest wins" guard usePoranayteV2.refresh uses, scoped per-tkId so a
  // stale call for an old tkId can only ever race against other calls for
  // that SAME old tkId, never against the current one's (see that file's
  // comment for the full reasoning — an out-of-order response here
  // previously had no guard at all, so a fast tab switch could apply a
  // stale rakenneosa list — one from a tkId the user already switched away
  // from — on top of the correctly-cleared current one).
  const latestTkIdRef = useRef(tkId);
  latestTkIdRef.current = tkId;
  const rakSeqByTkIdRef = useRef({});
  const refreshRak = useCallback(() => {
    // Clear synchronously on every tkId change (not just when it goes
    // falsy) — otherwise the auto-select-first-rakenneosa effect below can
    // see the PREVIOUS tarkastuskohde's still-stale rakenneosa list during
    // the fetch gap (rakId is already null by then, now that tab switches
    // apply optimistically/instantly) and lock onto one of its rakenneosa
    // instead of waiting for the new tarkastuskohde's own list.
    setRakenneosat([]);
    setRakenneosatTkId(tkId);
    const requestTkId = tkId;
    if (!requestTkId) return;
    const seq = (rakSeqByTkIdRef.current[requestTkId] || 0) + 1;
    rakSeqByTkIdRef.current[requestTkId] = seq;
    const isLatestForThisTkId = () => seq === rakSeqByTkIdRef.current[requestTkId] && requestTkId === latestTkIdRef.current;
    getRakenneosatV2(requestTkId)
      .then(r => { if (isLatestForThisTkId()) setRakenneosat(r.data); })
      .catch(() => {});
  }, [tkId]);
  useEffect(refreshRak, [refreshRak]);

  const openTk = useCallback((tk) => {
    changeNav({ ptsProjectId, kavId: tk.kavId, category: tk.category, tkId: tk.id, tkNimi: tk.nimi, rakId: null, rakNimi: null });
  }, [ptsProjectId, changeNav]);

  // Auto-select the tarkastuskohde matching the section this block is
  // embedded in — sections Kuntoarviovalikko creates are named exactly
  // after their tarkastuskohde (e.g. "Asuntojen parvekkeet"), same as how
  // v1 blocks inherited their project id from the section they were
  // created into. Only fires once (guarded by tkId already being set), so
  // it never overrides a tab the user picked manually afterwards.
  useEffect(() => {
    if (tkId || !activeSectionName || !tarkastuskohteet.length) return;
    const match = tarkastuskohteet.find(tk => tk.nimi === activeSectionName);
    if (match) openTk(match);
  }, [tkId, activeSectionName, tarkastuskohteet, openTk]);

  const addRak = useCallback(async (nimi, tyyppi) => {
    if (!nimi.trim()) return;
    setError('');
    try {
      const r = await createRakenneosaV2(tkId, nimi.trim(), tyyppi || null);
      refreshRak();
      changeNav({ ptsProjectId, kavId, category, tkId, tkNimi, rakId: r.data.id, rakNimi: nimi.trim() });
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  }, [tkId, ptsProjectId, kavId, category, tkNimi, changeNav, refreshRak]);

  const openRak = useCallback((rak) => {
    changeNav({ ptsProjectId, kavId, category, tkId, tkNimi, rakId: rak.id, rakNimi: rak.nimi });
  }, [ptsProjectId, kavId, category, tkId, tkNimi, changeNav]);

  // A selected rakId must actually belong to the current tarkastuskohde's
  // own rakenneosat list — never keep showing/using one left over from a
  // previous tarkastuskohde (e.g. a race during tab switching, or any
  // other path that left rakId stale). Clearing it here falls through to
  // the auto-select-first-rakenneosa effect right below, which picks a
  // real one for the current tarkastuskohde instead.
  useEffect(() => {
    if (rakenneosatTkId !== tkId) return; // rakenneosat is still for a different tk — not trustworthy yet
    if (!rakId || !rakenneosat.length) return;
    if (!rakenneosat.some(r => r.id === rakId)) {
      changeNav({ ptsProjectId, kavId, category, tkId, tkNimi, rakId: null, rakNimi: null });
    }
  }, [rakId, rakenneosat, rakenneosatTkId, ptsProjectId, kavId, category, tkId, tkNimi, changeNav]);

  // Auto-select the first rakenneosa the first time this block instance has
  // none chosen yet (rakId still null — e.g. freshly added to a
  // tarkastuskohde that already has a rakenneosa from Poränäyte). Only fires
  // while rakId is empty, so it never overrides one the user picked — and
  // once picked (here or by hand), that choice is saved into block.data.nav
  // through the same onChange the manual pill click already uses, which is
  // what makes it "remembered": nothing extra to persist, it's the same
  // block-data field the report already saves.
  useEffect(() => {
    if (rakenneosatTkId !== tkId) return; // rakenneosat is still for a different tk — not trustworthy yet
    if (rakId || !rakenneosat.length) return;
    openRak(rakenneosat[0]);
  }, [rakId, rakenneosat, rakenneosatTkId, tkId, openRak]);

  const goToPts = useCallback(() => changeNav({ ptsProjectId: null, kavId: null, category: null, tkId: null, tkNimi: null, rakId: null, rakNimi: null }), [changeNav]);
  const goToTk = useCallback(() => changeNav({ ptsProjectId, kavId: null, category: null, tkId: null, tkNimi: null, rakId: null, rakNimi: null }), [ptsProjectId, changeNav]);

  const currentPtsProject = ptsProjects.find(p => p.id === ptsProjectId) || null;

  return {
    nav: { ptsProjectId, kavId, category, tkId, tkNimi, rakId, rakNimi },
    // True while a tab/pill click's change is still only optimistic (not
    // yet confirmed by the real `value` prop catching up, or timed out).
    // The view uses this to disable further tab/pill clicks while one is
    // in flight — clicking again before the first one resolves can start
    // overlapping saves that resolve out of order, which is what caused
    // the UI to occasionally flicker back to a previous selection when
    // switching rakenneosa/tarkastuskohde quickly.
    navPending: !!optimisticNav,
    ptsProjects, currentPtsProject, rakTyypit, tarkastuskohteet, rakenneosat, error,
    linkedProjectId,
    pickPtsProject, createPtsProject,
    openTk, addRak, openRak,
    goToPts, goToTk,
    refreshTarkastuskohteet,
  };
}
